/**
 * 商家货号 → 款号。
 * <p>
 * 货号命名常混杂中文描述、尺码、颜色等后缀，例如：
 * - {@code 1001+1005常规-58} → {@code 1001+1005}
 * - {@code PH012-红-M} → {@code PH012}
 * - {@code ST-001-} → {@code ST-001}
 * <p>
 * 有「已知款号列表」时优先做最长前缀匹配（最准）；否则走启发式截断。
 */

/** CJK / 全角标点 / 假名 / 谚文等：货号里通常表示款号已结束、进入描述段 */
function isCjkOrFullwidthChar(ch) {
  if (!ch) return false;
  const code = ch.codePointAt(0);
  return (
    (code >= 0x4e00 && code <= 0x9fff) // CJK Unified
    || (code >= 0x3400 && code <= 0x4dbf) // Ext A
    || (code >= 0xf900 && code <= 0xfaff) // Compatibility
    || (code >= 0x3000 && code <= 0x303f) // CJK punctuation
    || (code >= 0xff00 && code <= 0xffef) // Half/fullwidth forms
    || (code >= 0x3040 && code <= 0x30ff) // Hiragana / Katakana
    || (code >= 0xac00 && code <= 0xd7af) // Hangul
  );
}

/** 款号结束后的分隔：空格、横杠、下划线、加号连接描述等 */
function isStyleBoundaryChar(ch) {
  if (!ch) return true;
  if (isCjkOrFullwidthChar(ch)) return true;
  return /[\s\-_+~/|，,、.·]/.test(ch);
}

/**
 * 启发式：从货号去掉中文描述段与末尾分隔符。
 * @param {string} supplierCode
 * @returns {string}
 */
export function heuristicStyleNoFromSupplierCode(supplierCode) {
  let value = String(supplierCode || '').trim();
  if (!value) return '';

  let cut = -1;
  for (let i = 0; i < value.length; ) {
    const cp = value.codePointAt(i);
    const ch = String.fromCodePoint(cp);
    if (isCjkOrFullwidthChar(ch)) {
      cut = i;
      break;
    }
    i += ch.length;
  }
  if (cut >= 0) {
    value = value.slice(0, cut);
  }

  // 去掉末尾分隔符（含连续「-」以及中文前残留的「-」）
  value = value.replace(/[\s\-_+~/|，,、.·]+$/g, '').trim();
  return value;
}

/**
 * 在已知款号中取最长前缀匹配（忽略大小写）。
 * 匹配后下一字符须为边界（结束 / 中文 / 分隔符），避免 {@code PH012A} 误匹配 {@code PH012}。
 * @param {string} supplierCode
 * @param {string[]} knownStyleNos
 * @returns {string} 命中的原始款号（保留模板写法）；未命中返回 ''
 */
export function matchKnownStyleNoPrefix(supplierCode, knownStyleNos = []) {
  const raw = String(supplierCode || '').trim();
  if (!raw) return '';
  const hay = raw.toLowerCase();
  const known = (Array.isArray(knownStyleNos) ? knownStyleNos : [])
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  if (!known.length) return '';

  let best = '';
  let bestLen = 0;
  for (const style of known) {
    const needle = style.toLowerCase();
    if (!needle || !hay.startsWith(needle)) continue;
    if (needle.length < bestLen) continue;
    if (needle.length === bestLen && style.length <= best.length) continue;
    const next = raw.slice(style.length, style.length + 2);
    const nextChar = next ? String.fromCodePoint(next.codePointAt(0)) : '';
    if (!isStyleBoundaryChar(nextChar)) continue;
    best = style;
    bestLen = needle.length;
  }
  return best;
}

/**
 * 从商家货号推导款号。
 * @param {string} supplierCode
 * @param {string[]|{ knownStyleNos?: string[] }} [knownOrOptions]
 *        已知模板款号列表，或 `{ knownStyleNos }`；有则优先最长前缀匹配
 * @returns {string}
 */
export function styleNoFromSupplierCode(supplierCode, knownOrOptions) {
  const raw = String(supplierCode || '').trim();
  if (!raw) return '';

  let knownStyleNos = [];
  if (Array.isArray(knownOrOptions)) {
    knownStyleNos = knownOrOptions;
  } else if (knownOrOptions && typeof knownOrOptions === 'object') {
    knownStyleNos = Array.isArray(knownOrOptions.knownStyleNos)
      ? knownOrOptions.knownStyleNos
      : [];
  }

  const matched = matchKnownStyleNoPrefix(raw, knownStyleNos);
  if (matched) return matched;

  return heuristicStyleNoFromSupplierCode(raw);
}
